import { useEffect, useRef } from 'react';

interface PageMetaProps {
  title: string;
  description?: string;
  keywords?: string;
  canonicalUrl?: string;
  ogType?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogUrl?: string;
  twitterCard?: string;
  jsonLd?: Record<string, unknown>[];
}

export default function PageMeta({
  title,
  description,
  keywords,
  canonicalUrl,
  ogType = 'website',
  ogTitle,
  ogDescription,
  ogImage,
  ogUrl,
  twitterCard = 'summary_large_image',
  jsonLd,
}: PageMetaProps) {
  const jsonLdRef = useRef<HTMLScriptElement | null>(null);

  useEffect(() => {
    document.title = title;

    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    const removeMeta = (name: string, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      const el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (el) el.remove();
    };

    if (description) setMeta('description', description);
    if (keywords) setMeta('keywords', keywords);

    if (canonicalUrl) {
      let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!link) {
        link = document.createElement('link');
        link.rel = 'canonical';
        document.head.appendChild(link);
      }
      link.href = canonicalUrl;
    }

    if (ogTitle || title) setMeta('og:title', ogTitle || title, true);
    if (ogDescription || description) setMeta('og:description', ogDescription || description || '', true);
    setMeta('og:type', ogType, true);
    if (ogUrl || canonicalUrl) setMeta('og:url', ogUrl || canonicalUrl || '', true);
    setMeta('og:site_name', '非麻瓜漫画', true);
    setMeta('og:locale', 'zh_CN', true);
    if (ogImage) setMeta('og:image', ogImage, true);

    setMeta('twitter:card', twitterCard);
    if (ogTitle || title) setMeta('twitter:title', ogTitle || title);
    if (ogDescription || description) setMeta('twitter:description', ogDescription || description || '');
    if (ogImage) setMeta('twitter:image', ogImage);

    if (jsonLd) {
      if (jsonLdRef.current) {
        jsonLdRef.current.remove();
      }
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(script);
      jsonLdRef.current = script;
    }

    return () => {
      removeMeta('og:title', true);
      removeMeta('og:description', true);
      removeMeta('og:type', true);
      removeMeta('og:url', true);
      removeMeta('og:site_name', true);
      removeMeta('og:locale', true);
      removeMeta('og:image', true);
      removeMeta('twitter:card');
      removeMeta('twitter:title');
      removeMeta('twitter:description');
      removeMeta('twitter:image');
      if (jsonLdRef.current) {
        jsonLdRef.current.remove();
      }
    };
  }, [title, description, keywords, canonicalUrl, ogType, ogTitle, ogDescription, ogImage, ogUrl, twitterCard, jsonLd]);

  return null;
}